import { Chip, Tooltip } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { visitaEstadoChipMap, VisitaEstado } from '../consts/visitasStatus';
import { getVisitaDisplayConfig, MotivoFalla } from '../utils/visitasUtils';

interface VisitaEstadoChipProps {
    estadoId: number;
    observacion?: string;
    motivoFalla?: MotivoFalla | null;
}

const VisitaEstadoChip = ({ estadoId, observacion, motivoFalla }: VisitaEstadoChipProps) => {
    const theme = useTheme();
    const chip = visitaEstadoChipMap[estadoId as VisitaEstado] || {
        textColor: 'white',
        backgroundColor: 'gray',
        text: 'Desconocido'
    };

    const displayConfig =
        estadoId === VisitaEstado.REALIZADA || estadoId === VisitaEstado.PENDIENTE
            ? getVisitaDisplayConfig(estadoId, motivoFalla ?? null)
            : null;
    const esFallida = displayConfig?.color === 'ambar';

    // SMP-179: una visita Pendiente con motivo de falla es una "Fallida" que volvio a Pendiente
    // para replanificarse (Escenario 2 y 4), asi que su chip debe leer "Fallida" y no "Pendiente".
    // La visita "En Revision" con motivo de falla (HU-2.1.02) conserva su label original con solo
    // una decoracion ambar.
    const label =
        esFallida && estadoId === VisitaEstado.PENDIENTE
            ? visitaEstadoChipMap[VisitaEstado.FALLIDA].text
            : chip.text;

    const amberTextColor = darken(theme.palette.warning.main, 0.4);
    const amberBackgroundColor = alpha(theme.palette.warning.main, 0.15);
    const amberBorderColor = alpha(theme.palette.warning.main, 0.5);

    const chipElement = (
        <Chip
            label={label}
            icon={esFallida ? <WarningAmberIcon sx={{ fontSize: 16, color: `${amberTextColor} !important` }} /> : undefined}
            sx={{
                backgroundColor: esFallida ? amberBackgroundColor : chip.backgroundColor,
                color: esFallida ? amberTextColor : chip.textColor,
                border: esFallida ? `1px solid ${amberBorderColor}` : undefined,
                fontWeight: 600,
                fontSize: 12,
                height: 26,
                borderRadius: 3,
                maxWidth: 160,
                '& .MuiChip-label': {
                    px: 1.5
                }
            }}
        />
    );

    if (esFallida && displayConfig?.tooltip) {
        return (
            <Tooltip
                title={displayConfig.tooltip}
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#384A52 !important',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-line',
                            maxWidth: 300
                        }
                    }
                }}
            >
                {chipElement}
            </Tooltip>
        );
    }

    if ([VisitaEstado.RECHAZADO, VisitaEstado.CANCELADA].includes(estadoId) && observacion) {
        return (
            <Tooltip
                title={observacion?.match(/\d{2}\/\d{2}\/\d{4}.*$/)?.[0] || observacion}
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#384A52 !important',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-line',
                            maxWidth: 300
                        }
                    }
                }}
            >
                {chipElement}
            </Tooltip>
        );
    }

    return chipElement;
};

export { VisitaEstadoChip };
